import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import {
  ChevronRight, ChevronLeft, Headphones, BookOpen, PencilLine, Mic, CheckCircle2, AlertCircle, Sparkles, Loader2, ArrowRight,
} from "lucide-react";

const ICONS = { Headphones, BookOpen, PencilLine, Mic };

// ============================================================
// Hub: list of 4 modules
// ============================================================
export default function IELTSGuidelinesHub() {
  const [modules, setModules] = useState(null);
  useEffect(() => { api.get("/ielts/guidelines").then((r) => setModules(r.data)); }, []);
  if (!modules) return <DashboardLayout><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></DashboardLayout>;
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="ielts-guidelines-hub">
        <div>
          <Link to="/app/ielts/start" className="text-xs text-slate-500 hover:text-[#F77F00]">← Change mode</Link>
          <h1 className="font-display text-4xl text-[#003049] mt-2">Test + Guidelines</h1>
          <p className="text-slate-600 mt-1">Step-by-step guidance for every IELTS section, written like a real teacher would explain it.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {modules.map((m) => {
            const Icon = ICONS[m.icon] || BookOpen;
            return (
              <Link key={m.id} to={`/app/ielts/guidelines/${m.id}`}
                data-testid={`guidelines-card-${m.id}`}
                className="group bg-white border border-slate-200 rounded-3xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${m.color}1A`, color: m.color }}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="font-display text-2xl text-[#003049]">{m.module}</div>
                </div>
                <div className="text-sm text-slate-600 mb-3">{m.summary}</div>
                <div className="text-xs text-slate-500">{m.stages_count} stages</div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: m.color }}>
                  Start <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// Wizard: stage-by-stage walkthrough
// ============================================================
export function IELTSGuidelinesWizard() {
  const { module } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/ielts/guidelines/${module}`).then((r) => setData(r.data));
  }, [module]);

  if (!data) return <DashboardLayout><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></DashboardLayout>;

  const stage = data.stages[idx];
  const Icon = ICONS[data.icon] || BookOpen;
  const isLast = idx === data.stages.length - 1;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid={`guidelines-wizard-${module}`}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link to="/app/ielts/guidelines" className="text-xs text-slate-500 hover:text-[#F77F00]">← All modules</Link>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${data.color}1A`, color: data.color }}>
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">{data.module} module</div>
            <h1 className="font-display text-3xl text-[#003049]">{data.module}</h1>
          </div>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5">
          {data.stages.map((_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full ${
              i < idx ? "bg-[#0EA5E9]" : i === idx ? "bg-[#F77F00]" : "bg-slate-200"
            }`} />
          ))}
        </div>
        <div className="text-xs text-slate-500">Stage {idx + 1} of {data.stages.length}: {stage.title}</div>

        {/* Stage card */}
        <div className="bg-white border border-slate-200 rounded-3xl p-7 space-y-5 min-h-[300px]" data-testid="guidelines-stage-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: data.color }}>
            <Sparkles className="w-3.5 h-3.5" /> Teacher Tip
          </div>
          <h2 className="font-display text-2xl text-[#003049]">{stage.title}</h2>
          {stage.body && <p className="text-slate-700 leading-relaxed">{stage.body}</p>}

          {stage.table && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              {stage.table.map((row, i) => (
                <div key={i} className={`grid grid-cols-[140px_1fr] ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                  <div className="px-4 py-3 text-sm font-semibold text-[#003049] border-r border-slate-200">{row[0]}</div>
                  <div className="px-4 py-3 text-sm text-slate-700">{row[1]}</div>
                </div>
              ))}
            </div>
          )}

          {stage.items && (
            <ul className="space-y-2">
              {stage.items.map((it, i) => (
                <li key={i} className="flex gap-3 text-slate-700 text-sm leading-relaxed">
                  <ChevronRight className="w-4 h-4 mt-1 shrink-0" style={{ color: data.color }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          )}

          {(stage.dos || stage.donts) && (
            <div className="grid md:grid-cols-2 gap-3">
              {stage.dos && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-2"><CheckCircle2 className="w-4 h-4" /> Do</div>
                  <ul className="space-y-1.5">
                    {stage.dos.map((d, i) => <li key={i} className="text-sm text-emerald-900">• {d}</li>)}
                  </ul>
                </div>
              )}
              {stage.donts && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm mb-2"><AlertCircle className="w-4 h-4" /> Don't</div>
                  <ul className="space-y-1.5">
                    {stage.donts.map((d, i) => <li key={i} className="text-sm text-rose-900">• {d}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage.cta && (
            <button onClick={() => nav(stage.cta.to)} data-testid="stage-cta-btn"
              className="mt-2 px-6 py-3 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white font-semibold inline-flex items-center gap-2">
              {stage.cta.label} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between">
          <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}
            data-testid="wizard-prev"
            className="px-5 py-2.5 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-2 text-sm">
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          {!isLast ? (
            <button onClick={() => setIdx((i) => Math.min(data.stages.length - 1, i + 1))}
              data-testid="wizard-next"
              className="px-5 py-2.5 rounded-full bg-[#003049] hover:bg-[#0C4B66] text-white inline-flex items-center gap-2 text-sm">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <span className="text-xs text-slate-500">End of guidance · use the CTA above to begin</span>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
